import React from "react";
import { graphql } from "gatsby";
import Layout from "@qlik/qlik/src/components/Layout";
import Seo from "@qlik/qlik/src/components/Seo";
import { renderLinkedComponents } from "@qlik/qlik/src/components/registry";
import { createI18nHelper } from "@qlik/utils/i18nHelper";
import { useI18n } from "@qlik/utils/i18nContext";
import isNoItem from "@qlik/qlik/src/lib/util/isNoItem";

/**
 * GraphQL query to retrieve all data for the flexible page.
 * Gatsby only allows one dynamic query per page.
 */
export const query = graphql`
    query($id: String, $headerName: String, $locale: String) {
        contentfulHeader(name: { eq: $headerName }, node_locale: { eq: $locale }) {
            ...Header
        }
        minimalFooter: contentfulFooter(name: { eq: "Minimal footer" }, node_locale: { eq: $locale }) {
            ...Footer
        }
        fullFooter: contentfulFooter(name: { eq: "Full footer" }, node_locale: { eq: $locale }) {
            ...Footer
        }
        pageData: contentfulPageFlexible(id: { eq: $id }) {
            name
            slug
            headerVariation {
                headerVariation
            }
            footerVariation {
                footerVariation
            }
            updatedAt
            metadata {
                ...SEO
            }
            components {
                __typename
                ...CardContainer
                ...CardDeckContainer
                ...ContextualContentWrapper
                ...RichTextContainer
                ...Multiplex
                ...Beta
            }
        }
        themeSettings: contentfulThemeSettings(node_locale: { eq: $locale }) {
            ...ThemeSettings
        }
    }
`;

/**
 * Helper function to determine whether content within a Contextual Content Wrapper should be displayed based on the current context.
 * Currently only supports language, but can be expanded in the future for locale or personalization.
 */
const shouldShowContextualContent = (wrapper, i18n) => {
    return wrapper?.languages?.includes("All") || wrapper?.languages?.includes(i18n.curr.enLangName);
};

/**
 * PageFlexible component
 * This component retrieves components from the registry based on content type and rendering name
 * @param {Object} data - The data object returned from the GraphQL query
 * @returns {JSX.Element} - The rendered PageFlexible component
 */
// const PageFlexible = ({ ...data.data, contentfulHeader, minimalFooter, fullFooter, pageData, themeSettings }) => {
const PageFlexible = ({ data: { contentfulHeader, minimalFooter, fullFooter, pageData, themeSettings } }) => {
    const { i18n } = useI18n();

    // Define page components for the main section of the page
    let pageComponents = pageData?.components
        // Take a first pass through the components, dealing with any ContextualContentWrapper content, i.e. if it shouldn't
        // be shown replace it in the array with null (to be filtered later), if it should, return an array of the subcomponents
        // (to be flattened in the next step).
        ?.map((el) => {
            // Contextual Content wrapper will place its rendered child modules into the list of pageComponents if they should be rendered based on the current context.
            if (el?.__typename === "ContentfulContextualContentWrapper") {
                return shouldShowContextualContent(el, i18n) ? el?.components?.map((subE) => subE) : null;
            } else {
                return el;
            }
        })
        ?.flat()
        // Render the components.
        ?.filter((x) => x)
        ?.map((el, i) => {
            // Filter out [[NoItem]] Ctas.
            if (el?.__typename === "ContentfulCardContainer" && el.cardItem) {
                el.cardItem.cta = el?.cardItem?.cta?.filter((cta) => !isNoItem(cta?.text));
                return renderLinkedComponents(el, i);
            }
            if (el?.__typename === "ContentfulCardDeckContainer") {
                el.cardItems = el?.cardItems?.map((cardItem) => ({
                    ...cardItem,
                    cta: cardItem?.cta?.filter((cta) => !isNoItem(cta?.text)),
                }));
                el.cta = el?.cta?.filter((cta) => !isNoItem(cta?.text));
                return renderLinkedComponents(el, i);
            }
            if (el?.__typename === "ContentfulMultiplex" && el.multiItems) {
                el.multiItems.cta = el?.multiItems?.cta?.filter((cta) => !isNoItem(cta?.text));
                return renderLinkedComponents(el, i);
            }

            return renderLinkedComponents(el, i);
        });

    // Define the header and footer variations (strings) based on page fields
    const headerType = pageData?.headerVariation?.headerVariation;
    const footerType = pageData?.footerVariation?.footerVariation;

    // Define the footer data based on the page fields
    const footerData = footerType === "Minimal" ? minimalFooter : fullFooter;

    return (
        <Layout
            headerType={headerType}
            headerData={contentfulHeader}
            footerType={footerType}
            footerData={footerData}
            pageSlug={pageData.slug}
            dynamicHeaderCta={themeSettings?.dynamicHeaderCta}
            searchPopupData={themeSettings?.searchPopupMicrocopyGroup}
        >
            <Seo data={pageData.metadata} pageSlug={pageData.slug} />
            {pageComponents}
        </Layout>
    );
};

// Handle `lang` attribute on HTML element
export function Head({ pageContext }) {
    const i18n = createI18nHelper(pageContext?.locale);

    return (
        <>
            <html lang={i18n?.curr?.langCode || "en"} />
        </>
    );
}

export default PageFlexible;
